import { ComponentProps, ReactNode } from "react";
import { v4 as uuidv4 } from "uuid";
import clsx from "clsx";
// Helpers
import { getRowsFromData } from "@/Washouts/helpers/getRowsFromData";
import { nullishResolver, and, or } from "@/Common/helpers/sonar";
import { prepareChargebacksData } from "@/Washouts/helpers/tables";
import { objToRecord, moneyColDef, numberColDef } from "@/Washouts/helpers/cards";
import { useIsWashoutEditable } from "@/Washouts/hooks/WashoutReview/useIsWashoutEditable";
// Components
import InformationCard from "@/Washouts/components/tables/Common/InformationCard";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
// MUI
import { Box, Divider, Grid, GridProps } from "@mui/material";
// Tables
import Compensation from "@/Washouts/components/tables/Common/CompensationTable";
import NewCarSalesTable from "@/Washouts/components/tables/Sales/Managers/NewCarSalesTable";
import SalesPerformanceTable from "@/Washouts/components/tables/Finance/Managers/SalesPerformanceTable";
import ChargebacksTable from "@/Washouts/components/tables/Common/ChargebacksTable";
import DepartmentalWashoutsTable from "@/Washouts/components/tables/Common/DepartmentalWashoutsTable";
import EmployeeMetaSection from "@/Washouts/components/EmployeeMetaSection";
import CallActivityPerformanceWrapper from "@/Washouts/components/CallActivityPerformanceWrapper";
import SpiffsTable from "@/Washouts/components/tables/Sales/Associates/SpiffsTable";
// Types
import { SalesManagerModel, SalesManagerWashoutContentProps, TableDefinition } from "@/Washouts/types/sales";
import { CompensationDataRecord } from "@/Washouts/components/tables/Common/CompensationTable/types";
import { StringMap } from "@/Common/interfaces/types";
import { BASE_TABLE_PROPS } from "@/Washouts/constants";
import { IColumnDefinition } from "@/Washouts/types/commons";
import { CardsDenseGrid } from "@/Washouts/layouts/CardsGrid";
import { resolveCardsData } from "@/Washouts/helpers/CardsGrid";
import { DeductionItem, LongevityInput } from "@/Washouts/types/CardsGrid";

const titles: StringMap<string> = {
	newCarsSales: "New Cars Sales",
	financeGross: "F&I Gross",
	preOwnedCarsSales: "Pre-Owned Cars Sales",
	variableOperationsCosts: "Variable Operations Costs",
};

const synchronizeTables = (table1: unknown, table2: unknown) => {
	const table1Display = getRowsFromData(table1);
	const table2Display = getRowsFromData(table2);

	let newRowCount = 0;
	if (table1Display?.length !== table2Display?.length) {
		const minTable = table1Display.length < table2Display?.length ? table1Display : table2Display;
		const maxRows = Math.max(table1Display?.length, table2Display?.length);
		while (minTable?.length < maxRows) {
			minTable?.push?.({
				_id: "synchronize-washout-row-" + newRowCount,
				emptyRow: true,
			});
			newRowCount++;
		}
	}
	return {
		table1: table1Display,
		table2: table2Display,
	};
};

function getTableDefinitionFromModel(
	model: SalesManagerModel,
	key: "financeGross" | "preOwnedCarsSales" | "variableOperationsCosts"
): TableDefinition | null {
	if (model?.[key]?.oems?.length) {
		return { key, table: model[key], totals: model[key]?.totals };
	}
	return null;
}

const getTablesToDisplay = (model?: SalesManagerModel) => {
	const tables: TableDefinition[] = [];
	if (model) {
		let def = getTableDefinitionFromModel(model, "financeGross");
		def && tables.push(def);
		def = getTableDefinitionFromModel(model, "preOwnedCarsSales");
		def && tables.push(def);
		def = getTableDefinitionFromModel(model, "variableOperationsCosts");
		def && tables.push(def);
		for (let i = 0; i < tables?.length; i += 2) {
			const table1 = tables[i];
			const table2 = tables?.[i + 1];
			const synchronized = synchronizeTables(table1?.table, table2?.table);
			tables[i].table = synchronized.table1;
			if (!table2) {
				tables[i + 1] = { key: "emptyTable", table: [], totals: { grossProfit: { value: 0 } } };
			}
			tables[i + 1].table = synchronized.table2;
		}
	}
	return tables;
};

const prepareTable = (table: TableDefinition, model?: SalesManagerModel, isLoading = true) => {
	return (
		<DepartmentalWashoutsTable
			rows={table?.table}
			totals={table?.totals}
			title={titles[table?.key]}
			loading={Boolean(isLoading)}
			dataPresent={Boolean(model)}
			tableRef={table?.key}
			showPVR={table?.key !== "variableOperationsCosts"}
		/>
	);
};

const prepareTableDisplay = (tables: TableDefinition[], model?: SalesManagerModel, isLoading = true) => {
	const setOfTables: ReactNode[][] = [];
	for (let i = 0; i < tables.length; i += 2) {
		const table1 = tables[i];
		const table2 = tables[i + 1];
		setOfTables.push([prepareTable(table1, model, isLoading), prepareTable(table2, model, isLoading)]);
	}
	return { setOfTables, count: tables.length };
};

const getGrossColumnTitle = (summary: CompensationDataRecord[] = []) => {
	const isVSG = summary.some((entry) => String(entry?.item)?.toUpperCase()?.includes("FIXED OPERATIONS"));
	return isVSG ? "Vol. Gross (VSG)" : "Vol. Gross (VG)";
};

function CrossSalesCards({ model, isLoading = false }: Readonly<SalesManagerWashoutContentProps>) {
	if (!model?.crossStoreSales?.cards) {
		return null;
	}
	const cards = model?.crossStoreSales?.cards;
	const pvrColDef: IColumnDefinition[] = cards.globalUnitCounts
		? [{ ...moneyColDef, field: "total", header: "Combined" }]
		: [
				{ ...moneyColDef, field: "new", header: "New", width: 100 },
				{ ...moneyColDef, field: "preOwned", header: "Pre-Owned", width: 100 },
			];
	const prodColDef: IColumnDefinition[] = cards.financeGlobalUnitCounts
		? [{ ...numberColDef, field: "total", header: "Combined" }]
		: [
				{ ...numberColDef, field: "new", header: "New", width: 100 },
				{ ...numberColDef, field: "preOwned", header: "Pre-Owned", width: 100 },
			];
	const allCombined = cards.globalUnitCounts && cards.financeGlobalUnitCounts;
	const allSeparated = !cards.globalUnitCounts && !cards.financeGlobalUnitCounts;
	return (
		<Grid
			container
			className={clsx("px-[30px] pb-[26px] pt-[8px] flex", {
				"gap-x-[76px]": allCombined || (cards.globalUnitCounts && !cards.financeGlobalUnitCounts),
				"justify-between": allSeparated || (cards.financeGlobalUnitCounts && !cards.globalUnitCounts),
			})}
		>
			<Grid item>
				<InformationCard
					title="Frontend PVR"
					columns={pvrColDef}
					rows={[objToRecord(cards.frontEndPvr)]}
					loading={isLoading}
					minRows={1}
					cardPadding={44}
					cellMargin={56}
				/>
			</Grid>
			<Grid item>
				<InformationCard
					title="Backend PVR"
					columns={pvrColDef}
					rows={[objToRecord(cards.backEndPvr)]}
					loading={isLoading}
					minRows={1}
					cardPadding={44}
					cellMargin={56}
				/>
			</Grid>
			<Grid item>
				<InformationCard
					title="Product Penetration"
					columns={prodColDef}
					rows={[objToRecord(cards.productPenetration)]}
					loading={isLoading}
					minRows={1}
					cardPadding={44}
					cellMargin={56}
				/>
			</Grid>
		</Grid>
	);
}

function CompensationSummary(props: Readonly<SalesManagerWashoutContentProps & ComponentProps<"div">>) {
	const { model = undefined, selected = null, isLoading = false, overrideCompensation = false, handleSummaryChange = () => {} } = props;

	const isEditable = useIsWashoutEditable({
		cycleStatus: selected?.status ?? "",
		positionStatus: model?.review?.status ?? "",
		hasRole: Boolean(overrideCompensation),
	});

	return (
		<Compensation
			data={model?.summary?.summary ?? []}
			addedData={model?.addedCompensations ?? []}
			tooltip
			editable={isEditable}
			columns={[
				{
					field: "gross",
					headerName: getGrossColumnTitle(model?.summary?.summary ?? []),
					align: "right",
					headerAlign: "left",
					cellsType: "dollar",
					width: 150,
					minWidth: 150,
					maxWidth: 150,
				},
				{
					field: "tier",
					headerName: "Eff. Inc. Rt.",
					align: "right",
					headerAlign: "right",
					cellsType: "percentage",
					width: 100,
					minWidth: 100,
					maxWidth: 100,
				},
			]}
			itemsDefinitions={[
				{ item: "Chargebacks Gross Commission", type: "percentage" },
				{ item: "Variable Gross Commissions", type: "percentage" },
				{ item: "Miami Stores USCER-Bonus", type: "percentage" },
				{ item: "Inbound Missed Calls", type: "percentage" },
			]}
			dataPresent={Boolean(model)}
			loading={isLoading}
			onActionUpdate={handleSummaryChange}
		/>
	);
}

const SpiffBonusSection = ({ model = undefined, isLoading = false }: Readonly<SalesManagerWashoutContentProps & GridProps>) => {
	if (!model?.spiffs?.spiffs && !isLoading) {
		return null;
	}

	return (
		<Grid item className="w-[646px]">
			<SpiffsTable
				key={`${model?._id}-spiffs`}
				data={model?.spiffs?.spiffs ?? []}
				dataPresent={Boolean(model)}
				loading={isLoading}
				tableProps={{ ...BASE_TABLE_PROPS }}
			/>
		</Grid>
	);
};

export default function SalesManagerWashoutContent(props: Readonly<SalesManagerWashoutContentProps>) {
	const {
		model = undefined,
		selected = null,
		isLoading = false,
		hideEmployeeMeta = false,
		handleSummaryChange = () => {},
		disableBoxShadow = false,
	} = props;
	const { setOfTables: washoutTables } = prepareTableDisplay(getTablesToDisplay(model), model, isLoading);

	if (and(!isLoading, !model)) {
		return null;
	}

	const chargeBackData = nullishResolver(prepareChargebacksData(model?.preOwnedChargebacks, model?.newChargebacks), {
		chargebacks: [],
		total: Number(0),
	});

	const showNewCarsSales = Boolean(model?.newCarsSales?.some?.((oemEntry) => oemEntry?.franchises?.length));

	return (
		<Grid
			container
			direction="column"
			rowSpacing={0}
			className={clsx({ "pb-[30px]": true, "shadow-white-section": !disableBoxShadow })}
		>
			<EmployeeMetaSection
				{...props.employeeMetaProps}
				hidden={hideEmployeeMeta}
				model={model}
				loading={isLoading}
				secondaryColumns={["DAYS_OUT"]}
			/>

			<Grid item>
				<NewCarSalesTable
					divider={showNewCarsSales}
					data={model?.newCarsSales}
					dataPresent={showNewCarsSales}
					loading={isLoading}
				/>
			</Grid>

			{isLoading ? (
				<Grid item container className="grid grid-cols-2 gap-x-[48px] gap-y-[32px] px-[30px] py-[15px]">
					<Grid className="w-[646px]">
						<TableLoader bigTitle toolbar buttons={2} content rows={8} />
					</Grid>
					<Grid className="w-[646px]">
						<TableLoader bigTitle toolbar buttons={2} content rows={8} />
					</Grid>
				</Grid>
			) : (
				<Grid item container className="grid grid-cols-2 gap-x-[48px] gap-y-[32px] px-[30px] py-[15px]">
					{washoutTables.map((pairOfTables) =>
						pairOfTables?.map?.((table) => (
							<Grid item key={uuidv4()} className="w-[646px]">
								{table}
							</Grid>
						))
					)}
				</Grid>
			)}

			<Grid item>
				{Boolean(model?.crossStoreSales?.sales?.length) && <Divider className="!mx-[24px] !border-b !border-[#CCC]"></Divider>}
				<SalesPerformanceTable
					divider={Boolean(model?.crossStoreSales?.sales?.length)}
					data={model?.crossStoreSales?.sales}
					columnVisibility={model?.crossStoreSales?.columnVisibility}
					dataPresent={Boolean(model?.crossStoreSales)}
					loading={isLoading}
					departmentalView
					emptyTitle="Cross Store Sales"
					useFullCollapseContainer
					tableProps={{ ...BASE_TABLE_PROPS }}
					beforeTableComponent={<CrossSalesCards {...props} />}
					useFrontProducts={true}
				/>
			</Grid>

			<CardsDenseGrid
				loading={isLoading}
				data={resolveCardsData({
					longevity: model?.longevity as LongevityInput,
					deductions: model?.deductions?.deductions as unknown as DeductionItem[],
				})}
			/>

			<Grid item container className="grid grid-cols-2 gap-x-[48px] gap-y-[32px] px-[30px] py-[15px]">
				<Grid item className="w-[646px]">
					<CallActivityPerformanceWrapper
						model={model}
						editable={or(selected?.status === "ACTIVE", selected?.status === "CLOSING")}
						dataPresent
						loading={isLoading}
						onActionUpdate={handleSummaryChange}
						tableProps={{ ...BASE_TABLE_PROPS, maxRows: 6 }}
					/>
				</Grid>

				<Grid item className="w-[646px]">
					<ChargebacksTable
						key={`${model?._id}-chargebacks`}
						data={chargeBackData?.chargebacks}
						staticFooter={Number(chargeBackData?.total) ? 0 : "-"}
						dataPresent={Boolean(model)}
						loading={isLoading}
						hideFooter={true}
						tableProps={{ ...BASE_TABLE_PROPS, maxRows: 6, enableRowsFiller: true }}
					/>
				</Grid>
			</Grid>

			<Grid item container direction="row" className="grid grid-cols-2 gap-x-[48px] gap-y-[32px] px-[30px] py-[15px]">
				<SpiffBonusSection model={model} isLoading={isLoading} />

				<Box className="flex justify-end">
					<Box className="w-[646px]">
						<CompensationSummary {...props} selected={selected} />
					</Box>
				</Box>
			</Grid>
		</Grid>
	);
}
